import { useEffect, useState } from 'react';
import { encodeFunctionData } from 'viem';
import { ethers } from 'ethers';

import { abiSpinWheel } from '../config/abi.contract';
import useWrappedRPC from './useWrappedRPC';
import useToken from './useToken';
import { calculateVRFFee, delay } from './useChest';
import { dispatchGameEvent } from '../utils/common';
import { getDailyWheelConfig } from '../services/userQuest.service';
import environments from '../utils/environments';
import { validateTokenWheelTransaction } from '../services/transaction.service';

const { SPIN_WHEEL_CONTRACT_ADDRESS } = environments;
const MAX_RETRIES = 30; // 30 seconds with 1s delay
const RETRY_DELAY_MS = 1000;

const spinWheelInterface = new ethers.utils.Interface(abiSpinWheel);

function useSpinWheel() {
  const { getXFISHTokenBalance } = useToken();
  const { sendTransactionWithGas, provider } = useWrappedRPC();
  const [spinWheelConfig, setSpinWheelConfig] = useState(null);

  // ---- Fetch Lucky Chest Configuration ---- //
  useEffect(() => {
    const fetchWheelConfig = async () => {
      try {
        const response = await getDailyWheelConfig();
        console.log('✅ Spin wheel config loaded:', response.data.data.listRewardWheelToken);
        setSpinWheelConfig(response.data.data.listRewardWheelToken);
      } catch (error) {
        console.error('❌ Error fetching Spin wheel config:', error);
      } finally {
      }
    };

    fetchWheelConfig();
  }, []);

  const spinTokenWheel = async () => {
    try {
      const { callbackGasLimit, gasPriceToFulfill, msgValue } = await calculateVRFFee();

      console.log(`[spinTokenWheel]`, { callbackGasLimit, gasPriceToFulfill, msgValue });
      // Encode transaction data
      const data = encodeFunctionData({
        abi: abiSpinWheel,
        functionName: 'spin',
        args: [callbackGasLimit, gasPriceToFulfill],
      });

      const txUiConfig = {
        header: 'Spin Token Wheel',
        description: `Spin token wheel`,
        buttonText: 'Spin',
      };

      const transactionRequest = {
        to: SPIN_WHEEL_CONTRACT_ADDRESS,
        value: msgValue,
        data,
      };

      // Send transaction
      const { txReceipt, receipt } = await sendTransactionWithGas(transactionRequest, txUiConfig);
      dispatchGameEvent({ type: 'confirmStartSpinTokenWheel', hasWaiting: true });

      // Get transaction receipt and extract request ID
      const eventSignature = spinWheelInterface.getEventTopic('SpinWheelRequested');
      const log = receipt.logs.find((l) => l.topics[0] === eventSignature);
      const parsedLog = spinWheelInterface.parseLog(log);

      console.log('parsedLog', parsedLog);
      // RequestId is the 2nd indexed parameter of the RandomNumberRequested event (3rd log)
      const requestId = parsedLog.args.requestId;

      if (!requestId) {
        throw new Error('Could not extract request ID from transaction receipt');
      }

      let karmaMultiplier = 1;
      try {
        const res = await validateTokenWheelTransaction({ wheelType: 'Token', transactionHash: txReceipt.hash });
        console.log('validate txn results:', res.data);
        const { karmaMultiplier: multiplier } = res.data.data;
        karmaMultiplier = multiplier;
      } catch (error) {
        console.warn('validateTokenWheelTransaction error:', error.message);
      }
      const { rewards } = await getTokenWheelRewardsByRequestId({
        requestId: requestId,
        txnHash: txReceipt.hash,
        karmaMultiplier,
      });

      const xFishTokenBalance = await getXFISHTokenBalance();

      if (!txReceipt?.hash) {
        throw new Error('Transaction failed on blockchain');
      }

      return {
        success: true,
        message: 'Spin successfully',
        data: {
          spinResult: {
            results: rewards,
          },
          xFishTokenBalance,
        },
      };
    } catch (error) {
      console.error('❌ Spin failed:', error);
      return {
        success: false,
        message: `Failed to spin: ${error.message || error}`,
      };
    }
  };

  // ---- Get Lucky Chest Rewards by Request Hash ---- //
  const getTokenWheelRewardsByRequestId = async ({ requestId, txnHash, karmaMultiplier }, maxRetries = MAX_RETRIES) => {
    console.log(`[getTokenWheelRewardsByRequestId] txnHash=${txnHash}`);
    let attempts = 0;

    const spinWheelContract = new ethers.Contract(SPIN_WHEEL_CONTRACT_ADDRESS, abiSpinWheel, provider);

    while (attempts < maxRetries) {
      attempts++;

      try {
        const result = await spinWheelContract.requestContexts(requestId);

        console.log(`[getTokenWheelRewardsByRequestId] result`, result);

        if (result?.generated) {
          console.log('✅ Spin reward found:', {
            user: result.user,
            requestId,
            randomSeed: result.randomSeed,
          });

          const reward = calculateRewardFromRandomNumber(result.randomSeed, spinWheelConfig, karmaMultiplier);

          console.log('reward', reward);
          return {
            ...result,
            rewards: reward.rewards,
            fromVRF: false,
          };
        }
      } catch (error) {
        console.error(`❌ Error on attempt ${attempts}:`, error.message);
      }

      // Wait before retry
      if (attempts < maxRetries) {
        console.log(`⏳ Attempt ${attempts}/${maxRetries} - Retrying in ${RETRY_DELAY_MS}ms...`);
        await delay(RETRY_DELAY_MS);
      }
    }

    throw new Error('Timeout waiting for spin rewards. Please check back later or contact support.');
  };

  return {
    spinTokenWheel,
  };
}

export default useSpinWheel;

// utils
/**
 * Mirrors BE logic
 * @param {string|number} randomNumber - Random number from Ronin's vRNG
 * @param {Array} rewardRates - wheel configs from BE
 * @returns {number} Reward amount (formatted as number, not wei)
 */
const calculateRewardFromRandomNumber = (randomNumber, rewardRates, multiplier) => {
  if (!rewardRates || rewardRates.length === 0) {
    throw new Error('Wheel configuration has no segments');
  }

  // Calculate total weight
  const totalWeight = rewardRates.reduce((sum, segment) => sum + segment.weight, 0);

  // Generate seeded random value using VRF
  const randomValue = getSeededRandom(randomNumber, totalWeight);

  // Find the winning segment using cumulative weight approach
  let cumulativeWeight = 0;
  for (const segment of rewardRates) {
    cumulativeWeight += segment.weight;
    if (randomValue <= cumulativeWeight) {
      // apply karma multiplier
      const rewardsWithMultiplier = segment.rewards.map((item) => ({
        ...item,
        multiplier,
        finalQuantity: Number(item.quantity) * multiplier,
      }));

      return {
        ...segment,
        rewards: rewardsWithMultiplier,
      };
    }
  }

  // Fallback to last segment if no match found (should never happen)
  if (segments.length === 0) {
    throw new Error('No segments available');
  }
  return segments[segments.length - 1] || null;
};

export const getSeededRandom = (randomSeed, maxValue) => {
  const seed =
    typeof randomSeed === 'string'
      ? BigInt(randomSeed) // supports 0x-prefixed hex
      : BigInt(randomSeed);

  const max = BigInt(Math.floor(maxValue * 1e6));

  const normalized = Number(seed % max) / 1e6;

  return normalized;
};
